import React from 'react';
import { DashboardCalendar } from '../../shared/ui-models/occupancy-dashboard.ui-model';
import {
  COST_DIMS,
  Card,
  ConversionControl,
  CostDim,
  EmptyRow,
  Metric,
  Segmented,
  Tag,
  VALUE_OPTIONS,
  ValueBasis
} from './components';
import { ConversionMode, CostBucket, DashboardModel, DashboardTotals } from './engine';
import { C, fmtDay, fmtK, fmtMoney, fmtNum, fmtPct } from './theme';

export interface CostTabProps {
  t: DashboardTotals;
  model: DashboardModel;
  valueBasis: ValueBasis;
  setValueBasis: (v: ValueBasis) => void;
  costDim: CostDim;
  setCostDim: (d: CostDim) => void;
  conversionMode: ConversionMode;
  setConversionMode: (m: ConversionMode) => void;
  fixedRate: number;
  setFixedRate: (r: number) => void;
  cal: DashboardCalendar;
}

const BLOCK_HEADERS = [
  'Unit',
  'Block type',
  'Description',
  'Raised by',
  'Dates',
  'Nights',
  'Expected loss',
  'Exposure'
];

export function CostTab({
  t,
  model,
  valueBasis,
  setValueBasis,
  costDim,
  setCostDim,
  conversionMode,
  setConversionMode,
  fixedRate,
  setFixedRate,
  cal
}: CostTabProps) {
  const data: CostBucket[] =
    costDim === 'reason' ? model.byReason : costDim === 'owner' ? model.byOwner : model.byProject;
  const val = (o: CostBucket) => (valueBasis === 'expected' ? o.expectedLoss : o.exposure);
  const maxVal = Math.max(1, ...data.map(val));
  const cls = (k: string) => model.byClass.find((c) => c.key === k);
  const operating = cls('operating');
  const contract = cls('contract');
  const unclassified = cls('unclassified');
  const dimLabel = (COST_DIMS.find((d) => d.value === costDim) || COST_DIMS[0]).label;

  return (
    <div className="mt-5 space-y-5">
      <div
        className="rounded-lg border bg-white px-4 py-3 text-xs"
        style={{ borderColor: C.line, color: C.muted }}
      >
        Block type comes from the owner-stay and maintenance flags on each block; any other block is
        &ldquo;Other&rdquo; and unclassified. Raised by is the person who created the block. Blocks
        carry no department yet, so costs cannot be split by department.
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Metric
          label="Blocked nights in range"
          value={fmtNum(t.blocked)}
          sub={`${t.blockCount} blocks · ${t.unitsWithBlocks} units`}
          accent={C.clay}
        />
        <Metric
          label="Expected loss"
          value={`EGP ${fmtK(t.expectedLoss)}`}
          sub={
            conversionMode === 'fixed'
              ? `At a flat ${fixedRate}% expected occupancy`
              : 'At actual occupancy by type & month'
          }
          accent={C.clay}
        />
        <Metric
          label="Exposure"
          value={`EGP ${fmtK(t.exposure)}`}
          sub="No conversion assumption applied"
        />
        <Metric
          label="Operating"
          value={`EGP ${fmtK(operating ? val(operating) : 0)}`}
          sub={operating ? `${fmtNum(operating.nights)} nights · maintenance` : '—'}
          accent="#B42318"
        />
        <Metric
          label="Contractual"
          value={`EGP ${fmtK(contract ? val(contract) : 0)}`}
          sub={contract ? `${fmtNum(contract.nights)} nights · owner stays` : '—'}
          accent={C.green}
        />
        <Metric
          label="Unclassified"
          value={`EGP ${fmtK(unclassified ? val(unclassified) : 0)}`}
          sub={unclassified ? `${fmtNum(unclassified.nights)} nights · no block type` : '—'}
          accent={C.muted}
        />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Segmented
          label="Break down by"
          value={costDim}
          onChange={setCostDim}
          options={COST_DIMS}
        />
        <Segmented
          label="Value at"
          value={valueBasis}
          onChange={setValueBasis}
          options={VALUE_OPTIONS}
        />
        {valueBasis === 'expected' && (
          <ConversionControl
            mode={conversionMode}
            setMode={setConversionMode}
            rate={fixedRate}
            setRate={setFixedRate}
          />
        )}
      </div>
      <Card
        title={`Cost by ${dimLabel.toLowerCase()}`}
        note="Ranked on the selected value basis, within the selected date range."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b" style={{ borderColor: C.line, background: '#FBFCFE' }}>
              <tr>
                {[
                  '',
                  'Nights',
                  'Share of nights',
                  'Blocks',
                  'Units',
                  'Avg length',
                  'Longest',
                  'Expected loss',
                  'Exposure',
                  'Share of loss'
                ].map((h, i) => (
                  <th
                    key={h + i}
                    className={`px-4 py-3 text-[11px] font-semibold ${
                      i === 0 ? 'text-left' : 'text-right'
                    }`}
                    style={{ color: C.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.key} className="border-b last:border-0" style={{ borderColor: C.line }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{d.key}</span>
                      {costDim === 'reason' && <Tag kind={d.costClass} />}
                    </div>
                    <div
                      className="mt-1.5 h-1.5 w-40 rounded-full"
                      style={{ background: '#F1F3F7' }}
                    >
                      <div
                        className="h-1.5 rounded-full"
                        style={{ width: `${(val(d) / maxVal) * 100}%`, background: C.clay }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(d.nights)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                    {fmtPct(d.shareNights, 0)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{d.blocks}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{d.units}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{d.avgLength.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{d.longest}</td>
                  <td
                    className="px-4 py-3 text-right font-medium tabular-nums"
                    style={{ color: C.clay }}
                  >
                    {fmtMoney(d.expectedLoss)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                    {fmtMoney(d.exposure)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtPct(d.shareLoss, 0)}</td>
                </tr>
              ))}
              {data.length === 0 && <EmptyRow cols={10} />}
            </tbody>
          </table>
        </div>
      </Card>
      <Card
        title="Every block in the range"
        note={`${model.costRecords.length} block records · export for the operations conversation`}
      >
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 z-10 border-b"
              style={{ borderColor: C.line, background: '#FBFCFE' }}
            >
              <tr>
                {BLOCK_HEADERS.map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[11px] font-semibold ${
                      i >= 5 ? 'text-right' : 'text-left'
                    }`}
                    style={{ color: C.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.costRecords
                .slice()
                .sort((a, b) => b.expectedLoss - a.expectedLoss)
                .map((r, i) => (
                  <tr
                    key={r.id + '-' + i}
                    className="border-b last:border-0 hover:bg-slate-50"
                    style={{ borderColor: C.line }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium leading-snug">{r.unitName}</p>
                      <p className="text-xs" style={{ color: C.muted }}>
                        {r.unitId}
                        {' · '}
                        {r.area}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">{r.reason}</span>
                        <Tag kind={r.costClass} />
                      </div>
                    </td>
                    <td
                      className="max-w-[260px] px-4 py-3 text-xs"
                      style={{ color: r.description ? C.ink : C.muted }}
                      title={r.description}
                    >
                      <span className="line-clamp-2">{r.description || '—'}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3" style={{ color: C.muted }}>
                      {r.owner}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs">
                      {fmtDay(cal, r.start)}
                      {' – '}
                      {fmtDay(cal, r.start + r.nights - 1)}
                      {(r.startedBefore || r.endsAfter) && (
                        <span className="ml-1.5" style={{ color: C.muted }}>
                          {r.startedBefore && r.endsAfter
                            ? '(spans range)'
                            : r.startedBefore
                            ? '(started earlier)'
                            : '(runs on)'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.nightsInRange}</td>
                    <td
                      className="px-4 py-3 text-right font-medium tabular-nums"
                      style={{ color: C.clay }}
                    >
                      {fmtMoney(r.expectedLoss)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                      {fmtMoney(r.exposure)}
                    </td>
                  </tr>
                ))}
              {model.costRecords.length === 0 && <EmptyRow cols={8} />}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
